import { LinkViewModel, LinksView, LinksViewProps, PageDataContext } from '@/services/RenderService'
import { CollectionDisplay as CollectionDisplayType } from '@/types/codegen-contentful'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import {
  isContentCollection,
  mapContentCollectionIdsToCollections,
  mapContentCollectionsToViewModels,
} from '../ContentCollection'
import { Banner } from './Banner'
import { CollectionHeader } from './CollectionHeader'
import { LandscapeRail } from './LandscapeRail'
import { LargeLandscapeGrid } from './LargeLandscapeGrid'
import { LargePortraitGrid } from './LargePortraitGrid'
import { LargePortraitRail } from './LargePortraitRail'

interface CollectionDisplayProps {
  collectionDisplay: CollectionDisplayType
  pageDataContext: PageDataContext
  containerPosition?: number
}

/**
 * Combines a list of data and a display format in order to render a collection
 */
export function CollectionDisplay({
  collectionDisplay,
  pageDataContext,
  containerPosition = 0,
}: CollectionDisplayProps) {
  if (!collectionDisplay.displayAs) return null

  const Ui = mapUiFunction(collectionDisplay.displayAs)
  if (!Ui) {
    logger().error(
      `A "CollectionDisplay" was provided with an unsupported "displayAs" option; nothing will be rendered.`,
      { collectionDisplay },
    )
    return null
  }

  const links = mapCollectionLinkData(collectionDisplay, pageDataContext)
  const collectionTitle = findCollectionTitle(collectionDisplay, pageDataContext)

  return (
    <>
      {collectionTitle && <CollectionHeader title={collectionTitle} />}
      <Ui
        id={collectionDisplay.sys.id}
        links={links}
        showTitleDescription={pageDataContext.contentItemConfig?.showThumbnailTitleDescription}
        containerPosition={containerPosition}
      />
    </>
  )
}

/**
 * Finds the appropriate data needed for the kind of collection and converts it to the shared view model
 */
function mapCollectionLinkData(
  collectionDisplay: CollectionDisplayType,
  pageDataContext: PageDataContext,
): LinkViewModel[] {
  if (isContentCollection(collectionDisplay.contentCollection)) {
    const contentCollections = mapContentCollectionIdsToCollections(
      pageDataContext,
      collectionDisplay.contentCollection?.contentCollectionId,
    )
    const linkViewModels = mapContentCollectionsToViewModels(contentCollections, collectionDisplay)
    const validLinkViewModels = linkViewModels.filter(isDefined)

    return validLinkViewModels
  }

  logger().error(`Failed to generate link view models for this collectionDisplay.`, { collectionDisplay })
  return []
}

/**
 * Matches the displayAs to the appropriate UI.
 *
 * For now displayAs values, add the UI of type LinksView here.
 */
function mapUiFunction(displayAs: string | undefined): LinksView | undefined {
  return {
    'large-portrait-rail': LargePortraitRail,
    'large-portrait-grid': LargePortraitGrid,
    'large-landscape-grid': LargeLandscapeGrid,
    'landscape-rail': LandscapeRail,
    'landscape-small': LandscapeRail,
    landscape: undefined,
    'landscape-with-subtext': LandscapeRailWithSubtext,
    'landscape-small-with-subtext': undefined,
    'landscape-grid-with-subtext': LandscapeGridWithSubtext,
    banner: Banner,
  }[displayAs ?? '']
}

/**
 * Finds the translated title from the CollectionService for a given CollectionDisplay.
 * Returns the title string if the CollectionDisplay is backed by a content-collection
 * that has a title, or null otherwise.
 */
export function findCollectionTitle(
  collectionDisplay: CollectionDisplayType,
  pageDataContext: PageDataContext,
): string | null {
  if (!isContentCollection(collectionDisplay.contentCollection)) return null

  const contentCollectionId = collectionDisplay.contentCollection?.contentCollectionId
  if (!contentCollectionId) return null

  const collections = pageDataContext['content-collection']
  if (!collections) return null

  for (const collection of collections) {
    if (collection?.id === contentCollectionId && collection?.title) {
      return collection.title
    }
  }

  return null
}

const LandscapeRailWithSubtext = (props: LinksViewProps) => {
  return <LandscapeRail {...props} showTitleDescription={true} />
}

const LandscapeGridWithSubtext = (props: LinksViewProps) => {
  return <LargeLandscapeGrid {...props} showTitleDescription={true} />
}
